import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BarsTransition } from '@tanium/react-loading-transition';
import { FormSelectInput } from '@tanium/react-form-select-input';
import { FormSection, FormRow } from '@tanium/react-form';
import { Box } from '@tanium/react-box';
import { Stack } from '@tanium/react-stack';
import get from 'lodash.get';
import debounce from 'lodash.debounce';
import { Field, FieldProps, FormikErrors, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { useQueryParam, StringParam } from 'use-query-params';
import { useTheme } from '@tanium/react-theme-context';
import Fuse from 'fuse.js';
import Customer from 'gql/Customer';
import IDropdownOption from 'common/IDropdownOption';
import {
  TARGET_ID,
  TARGET_NAME,
} from 'components/pages/request/requestPartials/createRequestFieldsPartials/Environments';
import { ACCESS_TYPE } from 'components/pages/request/requestPartials/createRequestFieldsPartials/AccessType';
import { ACCESS_TYPES } from 'components/pages/request/CreateRequestPage';
import RequestSlice from 'components/pages/request/store/RequestSlice';
import RequestSelectors from 'components/pages/request/store/RequestSelectors';
import testingMode from 'common/testingMode';
import CustomerSlice from 'components/pages/console/store/CustomerSlice';

const MAX_CUSTOMERS_SHOWN = 100;
const DEBOUNCE_TIMEOUT = 25;

export const CustomerEnvironment = (): React.ReactElement => {
  const dispatch = useDispatch();
  const customers: Customer[] = useSelector(RequestSelectors.selectCustomers());
  const customersProcessing = useSelector(RequestSelectors.selectCustomersProcessing);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [smallerCustomerList, setSmallerCustomerList] = useState<IDropdownOption[]>([]);
  const [targetIdQuery, setTargetIdQuery] = useQueryParam(TARGET_ID, StringParam);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setAccessTypeQuery] = useQueryParam(ACCESS_TYPE, StringParam);
  const { t } = useTranslation();
  const formik = useFormikContext();
  const theme = useTheme();
  const fuse = useMemo(
    () =>
      new Fuse<Customer>([], {
        includeMatches: true,
        ignoreLocation: true,
        keys: [
          { name: 'id', weight: 0.4 },
          { name: 'name', weight: 0.6 },
        ],
      }),
    []
  );

  const inputElement = useRef(null);
  const formInputIdPrefix = 'create-request-';

  // Get any needed metadata for this page on load
  useEffect(() => {
    if (!targetIdQuery) {
      dispatch(RequestSlice.actions.fetchCustomers());
    } else {
      dispatch(RequestSlice.actions.fetchCustomer({ id: targetIdQuery }));
    }
  }, [dispatch, targetIdQuery]);

  /**
   * Initializing Fuse collection set
   */
  useEffect(() => {
    fuse.setCollection(customers);
  }, [customers, fuse]);

  const transformListToOptions = (items: Customer[]) => {
    return items
      .map((customer: Customer) => {
        return {
          value: customer.id,
          label: `${customer.name}`,
          id: ` ID: ${customer.id}`,
          description: `Ring: ${customer.ring.name} | Region: ${customer.region} | Status: ${customer.status}`,
        };
      })
      .slice(0, MAX_CUSTOMERS_SHOWN);
  };

  // Get Customer By ID once the customer is selected
  const getCustomerDetails = (id: string) => {
    dispatch(CustomerSlice.actions.fetchCustomer({ id }));
  };

  /**
   * Takes in a list of customers to mutate into a dropdown list of MAX_CUSTOMERS_SHOWN values
   * if no list is provided, uses the customers list from the GQL call
   */
  const setInitialOptionList = (list = customers) => {
    if (!testingMode) {
      setSmallerCustomerList(transformListToOptions(list));
    }
  };

  const filteredCustomerList = (value: string): Customer[] => {
    // Get the items from the fuzzy search results
    const fuzzySearchArr = fuse.search(value).map((el) => el.item);
    // If fuzzy search has 0 items, show the original customer list (with MAX_CUSTOMERS_SHOWN)
    return fuzzySearchArr.length ? fuzzySearchArr : customers;
  };

  // Initializing targetId value and filtering the dropdown list to only the targetId option
  useEffect(() => {
    if (targetIdQuery) {
      setInitialOptionList(filteredCustomerList(targetIdQuery));
      if (!testingMode) {
        void formik.setFieldValue(TARGET_ID, targetIdQuery);
        getCustomerDetails(targetIdQuery);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers]);

  /**
   * Updates the options in the CE dropdown to the ones filtered by the user's search string
   * @param value - string that the user searched for
   */
  const updateOptions = (value: string): void => {
    setInitialOptionList(filteredCustomerList(value));
    isMenuOpen && (inputElement.current as any).focus();
  };

  /**
   * If there is currently only one customer fetched from URL, will load more
   * This is only called when menu is opened
   */
  const loadMore = (): void => {
    if (customers.length === 1) {
      dispatch(RequestSlice.actions.fetchCustomers());
    }
  };

  /**
   * Debounce the option update by DEBOUNCE_TIMEOUT miliseconds to minimize the Chrome tab memory consumption.
   * The user will visually feel no difference, but the input to memory usage is down by 80Mb
   *
   * Cannot use useCallback() here, as the debounce will cause the useRef to trigger twice, invalidating the .focus()
   */
  const debouncedUpdateOptions = debounce(updateOptions, DEBOUNCE_TIMEOUT);

  return (
    <FormSection title={t('SELECT_CUSTOMER')}>
      <Field name={TARGET_ID} data-testid={TARGET_ID}>
        {({ field, form }: FieldProps): React.ReactElement => (
          <FormRow
            label={t('CUSTOMER')}
            required
            description={t('CUSTOMER_ID_DESCRIPTION')}
            errors={(get(form.touched, field.name) && get(form.errors, field.name)) as FormikErrors<string>}
            labelId={`${formInputIdPrefix}${field.name}`}
          >
            {customersProcessing && (
              <div data-testid="bars-loading">
                <BarsTransition isLoading={true} />
              </div>
            )}
            <FormSelectInput
              isDisabled={customersProcessing}
              ref={inputElement}
              placeholder={t('CUSTOMER_ENVIRONMENT_PLACEHOLDER')}
              onInputChange={debouncedUpdateOptions}
              onChange={(option): void => {
                void formik.setFieldValue(TARGET_ID, option.value);
                void formik.setFieldValue(TARGET_NAME, option.label);
                void formik.setFieldValue(ACCESS_TYPE, '');
                void formik.setFieldValue(ACCESS_TYPES, []);
                setTargetIdQuery(option.value);
                getCustomerDetails(option.value);
                setAccessTypeQuery(undefined);
              }}
              onMenuOpen={() => {
                setIsMenuOpen(true);
                setInitialOptionList();
                loadMore();
              }}
              onMenuClose={() => {
                setIsMenuOpen(false);
              }}
              value={smallerCustomerList.find((option) => option.value === field.value)}
              inputId={`${formInputIdPrefix}${field.name}`}
              options={testingMode ? transformListToOptions(customers) : smallerCustomerList}
              aria-label={t('CUSTOMER')}
              formatOptionLabel={(
                option: {
                  value: number;
                  id: string;
                  label: string;
                  description: string;
                },
                state
              ) => {
                const isSelected =
                  state.context === 'menu' && Array.isArray(state.selectValue) && option === state.selectValue[0];
                const subtextColor = theme.colors[isSelected ? 'subdued' : 'subtle'];
                return (
                  <Stack direction="column" itemSpacing={0.5}>
                    <Box display="inline-flex">
                      <Box mr={1}>{option.label}</Box>
                      <Box color={subtextColor} fontSize={11}>
                        {option.id}
                      </Box>
                    </Box>
                    <Box color={subtextColor} fontSize={11}>
                      {option.description}
                    </Box>
                  </Stack>
                );
              }}
              styles={{
                // Either the input or the entered value must be absolute so they overlay each other
                // We need the TALLER one to be the relative one, so it makes the overall box stretch
                // (Hint: when using size="sm", divide theme.space[1] by 2 below.)
                singleValue: (base) => ({
                  ...base,
                  // Give it the theme margins since it is relative
                  marginTop: theme.space[1],
                  // Give it the theme margins since it is relative
                  marginBottom: theme.space[1],
                  // Make this one the relative one so the parent stretches
                  position: 'relative',
                  // Remove absolute positioning cruft
                  transform: 'none',
                  // Wrap the text in the selected option
                  whiteSpace: 'normal',
                }),
                input: (base) => ({
                  ...base,
                  // Overlay on top of the chosen value
                  position: 'absolute',
                  // Need to fiddle a little to get it to line up cursor with text
                  top: theme.space[1] - 2,
                  // Remove relative positioning cruft
                  paddingTop: 0,
                }),
              }}
            />
          </FormRow>
        )}
      </Field>
    </FormSection>
  );
};

export default CustomerEnvironment;
